import React, { useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.25); }
  70% { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0); }
  100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
`;

const SearchWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    height: 42px;
    min-width: 0;
    max-width: 320px;
    width: 100%;
    padding: 0 12px;
    gap: 8px;
    box-sizing: border-box;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    ${props => props.isFocused && css`
        border-color: #6366f1;
        box-shadow:
            0 0 0 3px rgba(99, 102, 241, 0.15),
            0 4px 12px rgba(99, 102, 241, 0.08);
        background: #fafafe;
    `}

    &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }

    @media (max-width: 995px) {
        min-width: 0;
        max-width: 100%;
        width: 100%;
    }
`;

const SearchIconWrapper = styled.span`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    color: ${props => props.isFocused ? '#6366f1' : '#94a3b8'};
    transition: color 0.2s ease;

    svg {
        width: 18px;
        height: 18px;
    }
`;

const StyledInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: 'Inter', 'Barlow', sans-serif;
    font-size: 13.5px;
    font-weight: 400;
    color: #1e293b;
    line-height: 1.5;
    min-width: 0;
    padding: 0;

    &::placeholder {
        color: #94a3b8;
        font-weight: 400;
    }

    &::-webkit-search-cancel-button {
        display: none;
    }
`;

const ClearButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: auto;
    background: #f1f5f9;
    border: none;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    padding: 0;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #64748b;

    &:hover {
        background: #e2e8f0;
        color: #1e293b;
    }

    svg {
        width: 12px;
        height: 12px;
    }
`;

const ShortcutBadge = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 6px;
  letter-spacing: 0.3px;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease;

  @media (max-width: 640px) {
    display: none;
  }
`;

const SearchInput = ({
    value = '',
    onChange,
    onClick,
    onEnter,
    placeholder = 'Search...',
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);

    const handleClear = () => {
        if (onChange) {
            onChange({ target: { value: '' } });
        }
        if (onEnter) {
            setTimeout(() => {
                onEnter({ key: 'Enter', preventDefault: () => {} });
            }, 0);
        }
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            if (value) {
                handleClear();
            } else if (inputRef.current) {
                inputRef.current.blur();
            }
        }
        if (onEnter) {
            onEnter(e);
        }
    };

    return (
        <SearchWrapper isFocused={isFocused}>
            <SearchIconWrapper
                isFocused={isFocused}
                onClick={(e) => {
                    if (onEnter) {
                        onEnter({ key: 'Enter', preventDefault: () => {} });
                    }
                }}
                style={{ cursor: onEnter ? 'pointer' : 'default' }}
            >
                <SearchIcon />
            </SearchIconWrapper>

            <StyledInput
                ref={inputRef}
                type="text"
                data-shortcut="search"
                title="Search (Alt + S)"
                value={value}
                placeholder={placeholder}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            />

            {!value && !isFocused && (
                <ShortcutBadge aria-hidden="true" title="Press Alt + S to search">
                    Alt + S
                </ShortcutBadge>
            )}

            {Boolean(value && value.length > 0) && (
                <ClearButton
                    type="button"
                    onClick={handleClear}
                    tabIndex={-1}
                    aria-label="Clear search"
                >
                    <CloseIcon />
                </ClearButton>
            )}
        </SearchWrapper>
    );
};

export default SearchInput;